if (!customElements.get('sale-banner-parallax')) {
  customElements.define(
    'sale-banner-parallax',
    class SaleBannerParallax extends HTMLElement {
      constructor() {
        super();

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.banner = this.closest('.sale-promo-banner');
        this.rows = Array.from(
          this.querySelectorAll('.sale-promo-banner__event-row')
        );
        this.prefersReducedMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

        this.populateRows();
        window.addEventListener('resize', this.onResize, { passive: true });

        if (this.prefersReducedMotion) {
          this.style.setProperty('--sale-banner-row-parallax-x', '0px');
          return;
        }

        this.isVisible = true;
        this.setupIntersectionObserver();
        this.update();
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);

        if (this.frame) {
          cancelAnimationFrame(this.frame);
          this.frame = null;
        }

        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }

      get rangeX() {
        const isMobile = window.innerWidth < 990;
        const value = isMobile ? this.dataset.rangeMobileX : this.dataset.rangeX;
        return parseFloat(value || '0');
      }

      onScroll() {
        if (!this.isVisible) return;
        this.requestTick();
      }

      onResize() {
        this.populateRows();
        if (this.prefersReducedMotion) return;

        this.requestTick();
      }

      setupIntersectionObserver() {
        if (!('IntersectionObserver' in window) || !this.banner) return;

        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.isVisible = entry.isIntersecting;

            if (this.isVisible) {
              this.requestTick();
            }
          },
          { rootMargin: '200px 0px' }
        );

        this.observer.observe(this.banner);
      }

      populateRows() {
        if (!this.banner || !this.rows?.length) return;

        const visibleRow =
          this.rows.find((row) => getComputedStyle(row).display !== 'none') ||
          this.rows[0];
        const word = (visibleRow.dataset.saleWord || 'SALE').trim();

        if (!word) return;

        const originalPattern = visibleRow.dataset.salePattern || '';
        visibleRow.dataset.salePattern = `${word} `;
        const singleWordWidth = visibleRow.scrollWidth || 1;
        visibleRow.dataset.salePattern = originalPattern;

        const isMobile = window.innerWidth < 990;
        const targetWidth =
          this.banner.clientWidth * (isMobile ? 2.7 : 2.25) +
          this.rangeX * 2 +
          160;
        const repeats = Math.max(
          isMobile ? 12 : 16,
          Math.ceil(targetWidth / singleWordWidth) + 1
        );
        const patternText = Array.from({ length: repeats }, () => word).join(' ');

        if (
          this.lastPatternText === patternText &&
          this.lastPopulateWidth === this.banner.clientWidth
        ) {
          return;
        }

        this.lastPatternText = patternText;
        this.lastPopulateWidth = this.banner.clientWidth;

        this.rows.forEach((row) => {
          if (row.textContent) row.textContent = '';

          row.dataset.salePattern = patternText;
        });
      }

      requestTick() {
        if (this.frame) return;

        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      update() {
        if (!this.banner) return;

        const rect = this.banner.getBoundingClientRect();
        const viewportHeight = window.innerHeight || 1;
        const bannerMidpoint = rect.top + rect.height / 2;
        const viewportMidpoint = viewportHeight / 2;
        const maxDistance = viewportMidpoint + rect.height / 2;
        const progress = (viewportMidpoint - bannerMidpoint) / maxDistance;
        const clamped = Math.max(-1, Math.min(1, progress));
        const offsetX = clamped * this.rangeX;

        this.style.setProperty(
          '--sale-banner-row-parallax-x',
          `${offsetX.toFixed(2)}px`
        );
      }
    }
  );
}
